import { Card, CardContent } from '@/components/ui/card'
import ForumIcon from '@app/assets/icons/ForumIcon'
import { useNavigate, useParams } from 'react-router-dom'
import Callout from './components/Callout'
import HomeContent from '../../../src/components/SectionContent'
import RelatedUseCaseItem from './components/RelatedUseCaseItem'
import { appLink } from '@app/config/appLink'
import { USE_CASES, USE_CASES_MAP } from './config/useCases.config'
import type { UseCaseId } from './types'
import { accentBgMap } from './types'

const UseCasesOverview = () => {
  const { useCaseId } = useParams<{ useCaseId: UseCaseId }>()
  const useCase = useCaseId ? USE_CASES_MAP[useCaseId] : null
  const navigate = useNavigate()

  if (!useCase) return <div>Use case not found: {useCaseId}</div>

  const { overviewBody, framingQuestion, roles, keyQuestions, relatedUseCaseIds, forumUrl } =
    useCase

  const relatedUseCases = USE_CASES.filter((uc) => relatedUseCaseIds.includes(uc.id))

  return (
    <>
      <HomeContent className=" pb-8 flex flex-col gap-10 ">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1fr_280px]">
          <div className="flex flex-col gap-4">
            <p className="text-sm leading-relaxed text-foreground">{overviewBody}</p>
            {framingQuestion && (
              <p className=" text-md italic font-medium text-rexBlue-800">{framingQuestion}</p>
            )}
          </div>

          {roles.length > 0 && (
            <Card className="h-fit bg-neutral-200 rounded-bl-none rounded-tl-none rounded-tr-none shadow-none border-muted ">
              <CardContent className="p-5">
                <p className="mb-2 text-sm font-semibold text-foreground">Roles include</p>
                <ul className="flex flex-col gap-1">
                  {roles.map((role) => (
                    <li key={role} className="flex items-start gap-2 text-sm text-foreground">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" />
                      {role}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </HomeContent>
      <div className="flex flex-col gap-4 bg-rexBlue-50">
        <HomeContent className="py-10">
          <h2 className="text-sm font-semibold text-rexBlue mb-5">Key questions</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {keyQuestions.map((question, i) => (
              <div
                key={i}
                className="border-l-4 border-rexBlue pl-3 text-sm leading-relaxed text-foreground"
              >
                {question}
              </div>
            ))}
          </div>
        </HomeContent>
        <div className="flex flex-col gap-4 bg-background py-8">
          {/* Section 3+4 — Community banner + Related use cases */}
          <HomeContent className="py-5">
            <div className="grid grid-cols-1 gap-4 items-start md:grid-cols-2">
              <Callout
                title="Connect with other Resilience Explorer® users"
                description="Ask questions, share experiences, and learn from other risk and resilience practitioners."
                actionLabel="Visit Forum"
                icon={<ForumIcon />}
                onActionClick={() => window.open(forumUrl, '_blank', 'noopener,noreferrer')}
              />
              <Card className="shadow-none">
                <CardContent className="p-4">
                  <p className="mb-3 text-sm font-semibold text-rexBlue-800">Related use cases</p>
                  <div className="flex flex-col gap-2">
                    {relatedUseCases.map((uc) => {
                      const Icon = uc.iconComponent
                      return (
                        <RelatedUseCaseItem
                          key={uc.id}
                          title={uc.title}
                          Icon={Icon}
                          accentBgClassName={accentBgMap[uc.accentColor]}
                          onView={() => navigate(`${appLink.homeUseCases.path}/${uc.id}/overview`)}
                        />
                      )
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </HomeContent>
        </div>
      </div>
    </>
  )
}

export default UseCasesOverview
